import React, { useEffect, useState } from "react";

import styled from "styled-components";

const Wrapper = styled.div`
  flex: 1;
  width: 100%;
  max-width: 400px;
  margin-top: 50px;
  padding: 40px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const Input = styled.input`
  align-self: stretch;
  outline: none;
  border: none;

  background: ${(props) => props.theme.colors.white}DD;
  color: ${(props) => props.theme.colors.black};
  border-radius: 2px;
  padding: 15px;

  font-size: 16px;
  font-weight: 600;
`;

interface PasswordProtectedContentProps {
  children: React.ReactNode;
  expectedPassword: string;
  localStorageId?: string;
}

// Hash password using Web Crypto API
const hashPassword = async (
  password: string,
  salt: string
): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password + salt);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  return hashHex;
};

const PasswordProtectedContent: React.FC<PasswordProtectedContentProps> = ({
  children,
  expectedPassword,
  localStorageId = "protected_content_password",
}) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");

  // Initialize logged-in state from localStorage
  useEffect(() => {
    const initializeLogin = async () => {
      try {
        const storedHash = localStorage.getItem(localStorageId);
        if (storedHash) {
          const expectedHash = await hashPassword(expectedPassword, localStorageId);
          if (storedHash === expectedHash) {
            setIsLoggedIn(true);
          }
        }
      } catch (err) {
        console.log(err);
      }
    };

    initializeLogin();
  }, [expectedPassword, localStorageId]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const passwordHash = await hashPassword(
        password.toLowerCase().trim(),
        localStorageId
      );
      const expectedHash = await hashPassword(
        expectedPassword.toLowerCase().trim(),
        localStorageId
      );

      if (passwordHash === expectedHash) {
        localStorage.setItem(localStorageId, passwordHash);
        setIsLoggedIn(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  if (isLoggedIn) {
    return <>{children}</>;
  }

  return (
    <Wrapper>
      <Form onSubmit={onSubmit}>
        <Input
          type="password"
          placeholder="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <button type="submit">Login</button>
      </Form>
    </Wrapper>
  );
};

export default PasswordProtectedContent;
